import type { Country } from "../Country.js";
import type { Currency } from "../Currency.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { OrderProduct } from "../OrderProduct.js";
import type { OrderProductAttribute } from "../OrderProductInfo.js";
import type { ProductOfVariantForCustomer } from "../Product.js";
import type { SubcategoryForCustomer } from "../Subcategory.js";
import { getPriceForCustomerOfProductVariant } from "./getPriceForCustomerOfProductVariant.js";
import { getProductLegacyId } from "./getProductLegacyId.js";

export interface OrderProductSettings {
	currency: Currency;
	destinationCountry: Country | "outsideEu";
	subcategories: SubcategoryForCustomer[];
	vatType: EconomicSubjectVatType;
}

export function transformToOrderProduct(
	product: ProductOfVariantForCustomer,
	attributes: OrderProductAttribute[],
	{ destinationCountry, currency, subcategories, vatType }: OrderProductSettings,
	quantity: number,
): OrderProduct {
	const subcategory = subcategories.find(({ id }) => product.subcategoryId === id);
	if (!subcategory) {
		throw new Error(
			`Cannot transform product: ${product.id} to order product because subcategory with id: ${product.subcategoryId} was not found.`,
		);
	}

	return {
		id: crypto.randomUUID(),
		info: {
			category: subcategory.category,
			id: product.id,
			name: product.name,
			price: getPriceForCustomerOfProductVariant(product, currency, destinationCountry, vatType),
			subcategoryId: product.subcategoryId,
			variantType: product.variantType,
			variant: {
				id: product.variant.id,
				label: product.variant.label,
				weightInGrams: product.variant.weightInGrams,
			},
			attributes: attributes.map((group) => {
				const groupInProduct = product.attributeGroups.find(({ id }) => group.id === id);
				if (!groupInProduct) {
					throw new Error(`Cannot transform product: ${product.id} to order product with invalid attribute group id.`);
				}
				const valueInProduct = groupInProduct.values.find(({ id }) => id === group.value.id);
				if (!valueInProduct) {
					throw new Error(`Cannot transform product: ${product.id} to order product with invalid attribute value id.`);
				}
				return {
					id: group.id,
					type: groupInProduct.type,
					value: {
						id: valueInProduct.id,
						label: valueInProduct.label,
						value: valueInProduct.value,
					},
				};
			}),
			legacyId: getProductLegacyId(product, attributes),
		},
		quantity,
	};
}
